import styles from './ctf-rich-text.module.css'
import { BLOCKS, type Document, INLINES } from '@contentful/rich-text-types'
import {
  type Options,
  documentToReactComponents,
} from '@contentful/rich-text-react-renderer'
import { YouTubeVideo, isYoutubeEmbeddable } from './youtube'
import {
  ArticleAsset,
  ArticleImage,
} from '@app/insights/_components/article-image'
import { type ComponentLogoWithSpec, type Asset } from '@lib/__generated/sdk'
import { createElement, Fragment } from 'react'
import { cn } from '@lib/utils'

export type EmbeddedEntryType = ComponentLogoWithSpec | Asset | null

export type CtfJsonDocument = Document

export type CtfRichtextAllowedInstrinsicElements =
  | 'article'
  | 'div'
  | 'section'
  | 'fragment'

interface AsProp<T extends CtfRichtextAllowedInstrinsicElements> {
  as?: T
}

export type CtfRichTextStyling = 'article' | 'margins-only' | 'none'

export interface ContentfulRichTextInterface {
  json: CtfJsonDocument
  links?:
    | {
        entries: {
          block: EmbeddedEntryType[]
        }
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any
  className?: string
  styling?: CtfRichTextStyling
}

export const EmbeddedEntry = (entry: EmbeddedEntryType) => {
  // eslint-disable-next-line sonarjs/no-small-switch
  switch (entry?.__typename) {
    case 'ComponentLogoWithSpec':
      return <ArticleImage image={entry} />

    case 'Asset':
      return <ArticleAsset image={entry} />
    default:
      return null
  }
}

export const contentfulBaseRichTextOptions = ({
  links,
}: ContentfulRichTextInterface): Options => ({
  renderNode: {
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const entry = links?.entries?.block?.find(
        (item: EmbeddedEntryType) => item?.sys?.id === node.data.target.sys.id,
      ) as EmbeddedEntryType
      if (entry == null) return null

      return <EmbeddedEntry {...entry} />
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const entry = links?.assets?.block?.find(
        (item: EmbeddedEntryType) => item?.sys?.id === node.data.target.sys.id,
      ) as EmbeddedEntryType
      if (entry == null) return null
      return <EmbeddedEntry {...entry} />
    },
    [BLOCKS.PARAGRAPH]: (node, children) =>
      node.content.some(
        (c) => c.nodeType === 'hyperlink' && isYoutubeEmbeddable(c.data.uri),
      ) ? (
        <div className='prose'>{children}</div>
      ) : (
        <p>{children}</p>
      ),
    [INLINES.HYPERLINK]: (node) =>
      isYoutubeEmbeddable(node.data.uri) ? (
        <YouTubeVideo url={node.data.uri} />
      ) : (
        <a href={node.data.uri} target='about:blank' rel='noopener noreferrer'>
          {(node.content[0] as { value: string }).value}
        </a>
      ),
  },
  renderText: (text: string) =>
    text
      .split('\n')
      // eslint-disable-next-line unicorn/no-array-reduce
      .reduce(
        (children: React.ReactNode[], textSegment: string, index: number) => [
          ...children,
          index > 0 && <br key={index} />,
          textSegment,
        ],
        [],
      ),
})

export const CtfRichText = <
  As extends CtfRichtextAllowedInstrinsicElements = 'article',
>({
  json,
  links,
  className,
  as,
  styling = 'article',
}: ContentfulRichTextInterface & AsProp<As>) => {
  const baseOptions = contentfulBaseRichTextOptions({ links, json })

  if (as === 'fragment') {
    return <Fragment>{documentToReactComponents(json, baseOptions)}</Fragment>
  }

  // TODO: fix color when device is in dark mode. The color is washed out since we do not support dark mode yet in our components.
  return createElement(
    as ?? 'div',
    {
      className: cn(
        styles.ctf,
        'max-w-none',
        {
          prose: styling !== 'none',
          [styles.margins]: styling === 'article' || styling === 'margins-only',
          [styles.font]: styling === 'article',
        },
        className,
      ),
    },
    documentToReactComponents(json, baseOptions),
  )
}
